// Логика чатов спецификации, общая для трёх ролей (ТЗ 6.10): список, лента, отправка сообщения
// с вложениями, создание трёхстороннего чата дизайнером, текст системного события (ТЗ 8.3).
// Роль передаётся вызывающим: источник данных у поставщика и заказчика другой (этапы 8 и 9),
// а сборка текстов событий и загрузка вложений — одни и те же. Сгенерировано Claude.
import specificationApi from '@/api/specification';
import specChatApi from '@/api/specChat';
import specChatMessageApi from '@/api/specChatMessage';
import fileApi from '@/api/file';
import { money } from '@/utils/spec';

/** Тексты системных событий по кодам ТЗ 8.3 — одинаковы для всех ролей. */
const EVENT_TEXTS = {
  requested: (data) => (data.repeat ? 'Позиции запрошены повторно' : 'Отправлен запрос КП'),
  quote_uploaded: (data) => (data.insteadOfConfirmation
    ? 'Поставщик загрузил новое КП вместо подтверждения'
    : 'Поставщик загрузил файл КП'),
  offers_sent: (data) => `Поставщик отправил отклики: ${data.offersCount ?? ''}`.trim(),
  offer_changed: () => 'Поставщик изменил отправленный отклик',
  rework: (data) => `Позиции отправлены на доработку${data.comment ? `: ${data.comment}` : ''}`,
  // отклик выигрывает при утверждении заказчиком, а не при выборе дизайнером
  offer_chosen: () => 'Заказчик утвердил позицию с вашим откликом',
  discount_changed: () => 'Изменено разделение скидки',
  offers_lost: (data) => `Отклики не выбраны${data.reason ? `: ${data.reason}` : ''}`,
  offer_withdrawn: () => 'Поставщик отозвал отклик',
  customer_approved: () => 'Заказчик утвердил позиции',
  supplier_confirmed: () => 'Поставщик подтвердил позицию',
  items_deleted: () => 'Из запроса удалены позиции',
  customer_review: (data) => (data.changed
    ? `Предложение по позиции изменено${data.customerPrice ? `, цена ${money(data.customerPrice)}` : ''}`
    : 'Позиция отправлена на согласование заказчику'),
  customer_rejected: (data) => `Заказчик отклонил позицию${data.reason ? `: ${data.reason}` : ''}`,
  back_to_selection: (data) => `Позиция вернулась в подбор${data.reason ? `: ${data.reason}` : ''}`,
};

export default {
  /** Чаты спецификации глазами дизайнера (ТЗ 4.1). */
  fetchDesignerChats(specificationId) {
    return specificationApi.fetchChats(specificationId).then((r) => r.items ?? []);
  },

  fetchMessages(chatId) {
    return specChatApi.fetchMessages(chatId).then((r) => r.items ?? []);
  },

  /**
   * Загрузка вложений сообщения — общей операцией с назначением «спецификация» (ТЗ 9.1).
   * Последовательно: файлов немного, а порядок вложений в сообщении должен сохраниться.
   */
  uploadFiles(files) {
    return Array.from(files ?? []).reduce(
      (chain, file) => chain.then((acc) => fileApi.createForSpecification(file)
        .then((result) => [...acc, `/api/files/${result.id}`])),
      Promise.resolve([]),
    );
  },

  /** Сообщение: текст и/или вложения (ТЗ 4.1). */
  async sendMessage(chatId, text, files = []) {
    const fileIris = await this.uploadFiles(files);
    return specChatMessageApi.create({
      chat: `/api/spec_chats/${chatId}`,
      text: text || null,
      files: fileIris,
    });
  },

  /** Трёхсторонний чат с первым сообщением создаёт только дизайнер (ТЗ 3.2, 4.1). */
  async createTripartite(specificationId, supplierId, text, files = []) {
    const fileIris = await this.uploadFiles(files);
    return specChatApi.create({
      specification: `/api/specifications/${specificationId}`,
      supplier: `/api/suppliers/${supplierId}`,
      text: text || null,
      files: fileIris,
    });
  },

  /** Текст системного события для ленты (ТЗ 8.3). */
  eventText(message) {
    const data = message?.eventData ?? {};
    const build = EVENT_TEXTS[message?.eventType];
    return build ? build(data) : 'Событие по спецификации';
  },

  /** Позиции события: до 5 названий, далее «и ещё N» (ТЗ 8.3). */
  eventItems(message) {
    const data = message?.eventData ?? {};
    const list = data.items ?? (data.item ? [data.item] : []);
    return list.slice(0, 5);
  },

  eventRestCount(message) {
    const data = message?.eventData ?? {};
    const list = data.items ?? (data.item ? [data.item] : []);
    return Math.max(0, list.length - 5);
  },
};
